import React, { useState, useMemo } from 'react';
import { Modal, Button, DatePicker, Segmented, Alert, message, Typography } from 'antd';
import {
    LockOutlined,
    CalendarOutlined,
    FieldTimeOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { Method_POST_CONTEXTO } from '../../../../service/api';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useDraggableModal } from "../Balancete/utils/modalDraggable";


dayjs.locale('pt-br');
dayjs.extend(isSameOrBefore);

const { Text } = Typography;
const { RangePicker } = DatePicker;

const ModalBloquearPeriodo = ({ show, handleClose, onUpdate, zIndex, onFocus }) => {
    const draggableProps = useDraggableModal(zIndex, onFocus, {
        show,
        modalKey: 'bloqueio',
        initialWidth: 520,
        initialHeight: 350,
        overflow: 'visible'
    });
    const [mode, setMode] = useState('year'); // year | custom
    const [dateValue, setDateValue] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    const description = useMemo(() => {
        if (!dateValue) return null;

        if (mode === 'year') {
            return (
                <span>
                    Você está prestes a <b>FECHAR</b> todos os períodos de
                    <b> {dateValue.format('YYYY')}</b>.
                </span>
            );
        }

        if (mode === 'custom' && dateValue?.[0] && dateValue?.[1]) {
            return (
                <span>
                    Você está prestes a <b>FECHAR</b> os períodos de
                    <b> {dateValue[0].format('MM/YYYY')}</b> até
                    <b> {dateValue[1].format('MM/YYYY')}</b>.
                </span>
            );
        }

        return null;
    }, [dateValue, mode]);

    const handleBloquear = async () => {
        if (!dateValue) {
            setErrorMsg('Selecione o período para bloqueio.');
            return;
        }

        setLoading(true);
        setErrorMsg(null);

        try {
            let dataInicio;
            let dataFim;

            if (mode === 'year') {
                dataInicio = dateValue.startOf('year');
                dataFim = dateValue.endOf('year');
            } else {
                dataInicio = dateValue[0].startOf('month');
                dataFim = dateValue[1].endOf('month');
            }

            // percorre mês a mês
            let cursor = dataInicio.clone().startOf('month');

            while (cursor.isSameOrBefore(dataFim, 'month')) {
                await Method_POST_CONTEXTO(
                    `/empresa/contabilidade/bloquear/periodo?ano=${cursor.year()}&mes=${cursor.month() + 1}`
                );
                cursor = cursor.add(1, 'month');
            }

            message.success("Período(s) bloqueado(s) com sucesso!");
            if (onUpdate) onUpdate();
            handleCancel();

        } catch (err) {
            console.error(err);
            setErrorMsg("Erro ao bloquear o período.");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setDateValue(null);
        setErrorMsg(null);
        handleClose();
    };

    return (
        <Modal
            open={show}
            onCancel={handleCancel}
            width={520}
            {...draggableProps}
            styles={{ body: { overflow: 'visible' } }}
            title={
                <span style={{ color: '#faad14' }}>
                    <LockOutlined style={{ marginRight: 8 }} />
                    Bloquear Período Contábil
                </span>
            }
            footer={[
                <Button key="cancel" onClick={handleCancel} disabled={loading}>
                    Cancelar
                </Button>,
                <Button
                    key="confirm"
                    type="primary"
                    danger
                    icon={<LockOutlined />}
                    loading={loading}
                    onClick={handleBloquear}
                    disabled={!dateValue}
                >
                    Bloquear
                </Button>
            ]}
        >
            {errorMsg && (
                <Alert
                    type="error"
                    showIcon
                    message="Erro"
                    description={errorMsg}
                    style={{ marginBottom: 16 }}
                />
            )}

            <Alert
                type="warning"
                showIcon
                icon={<ExclamationCircleOutlined />}
                description={
                    description ||
                    "Selecione um período para bloquear. Após o bloqueio, não será possível incluir ou alterar lançamentos."
                }
                style={{ marginBottom: 24 }}
            />

            <Text strong>Modo de bloqueio:</Text>

            <Segmented
                block
                style={{ margin: '12px 0 16px' }}
                value={mode}
                onChange={(val) => {
                    setMode(val);
                    setDateValue(null);
                }}
                options={[
                    { label: 'Ano Completo', value: 'year', icon: <CalendarOutlined /> },
                    { label: 'Período Específico', value: 'custom', icon: <FieldTimeOutlined /> },
                ]}
            />

            {mode === 'year' ? (
                <DatePicker
                    picker="year"
                    style={{ width: '100%' }}
                    value={dateValue}
                    onChange={setDateValue}
                    format="YYYY"
                    getPopupContainer={trigger => trigger.parentElement}
                />
            ) : (
                <RangePicker
                    picker="month"
                    style={{ width: '100%' }}
                    value={dateValue}
                    onChange={setDateValue}
                    format="MM/YYYY"
                    getPopupContainer={trigger => trigger.parentElement}
                />
            )}
        </Modal>
    );
};

export default ModalBloquearPeriodo;
